/**
 * Format: Tracking Items
 * -----------------------------------------------------------------------------
 * Formats product and variants for tracking items.
 *
 */

/**
 * Format: Tracking Items.
 * @param {Object} [index] - index of the item.
 * @param {Object} [product] - product object.
 * @param {Object} variant - variant object.
 * If product object is not provided, variant object should contain product_id,
 * product_title, vendor, product_type, availableState.
 * @param {Object} [collection] - collection object.
 * @param {String} [template] - template string.
 * @param {Object} discountObject - discount object contains
 * percentageDiscount and discountCode.
 * @param {Number} [quantity] - quantity of the item.
 * @param {String} [deliveryTime] - delivery time of the item.
 * @param {String} [currencyCode] - currency code of the item.
 * @returns {Object}
 */
export function formatTrackingItem({
  index = 0,
  product,
  variant,
  collection,
  template,
  discountObject,
  quantity = 1,
  deliveryTime,
  currencyCode,
} = {}) {
  try {
    if (typeof variant === 'undefined' || variant === null) {
      return {};
    }

    const productObject = product || {
      id: variant.product_id,
      title: variant.product_title,
      vendor: variant.vendor,
      type: variant.product_type,
      availableState: variant.availableState,
    };
    const price = variant.price.amount || variant.price || 0;
    let compareAtPrice = 0;
    if (typeof variant.compare_at_price !== 'undefined') {
      compareAtPrice =
        variant.compare_at_price?.amount || variant.compare_at_price || 0;
    } else if (typeof variant.compareAtPrice !== 'undefined') {
      compareAtPrice =
        variant.compareAtPrice?.amount || variant.compareAtPrice || 0;
    }
    const percentageDiscount = discountObject.percentageDiscount || 0;
    const itemDiscount = compareAtPrice > price ? compareAtPrice - price : 0;
    const itemCoupon = discountObject.discountCode || '';
    const couponValue = Math.round((percentageDiscount / 100) * price);
    const totalDiscount = (itemDiscount + couponValue) / 100;

    let itemSize = '-';
    if (typeof variant.options_with_values !== 'undefined') {
      itemSize =
        variant.options_with_values.find((option) => {
          if (
            cnvs.product.clothingSizeOptions.includes(
              option.name.toLowerCase(),
            ) ||
            cnvs.product.sizeOptions.includes(option.name.toLowerCase())
          ) {
            return true;
          }
          return false;
        })?.value || '-';
    } else if (typeof variant.selectedOptions !== 'undefined') {
      itemSize =
        variant.selectedOptions.find((option) => {
          if (
            cnvs.product.clothingSizeOptions.includes(
              option.name.toLowerCase(),
            ) ||
            cnvs.product.sizeOptions.includes(option.name.toLowerCase())
          ) {
            return true;
          }
          return false;
        })?.value || '-';
    }

    let itemStatus = '-';
    if (productObject.availableState === 'sold-out') {
      itemStatus = 'out of stock';
    } else if (totalDiscount > 0) {
      itemStatus = 'on sale';
    } else if (variant.badges?.length > 0) {
      itemStatus = variant.badges.join(', ');
    }

    return {
      item_name: productObject.title || '-',
      item_id: variant.sku || '-',
      item_variant_id: variant.id || '-',
      discount: totalDiscount || 0,
      affiliation: 'ESN-Shop',
      coupon: itemCoupon || '-',
      coupon_value: couponValue / 100 || 0,
      currency: currencyCode || '-',
      item_brand: productObject.vendor || '-',
      item_category: productObject.type || '-',
      item_size: itemSize || '-',
      item_variant: variant?.title || '-',
      item_status: itemStatus || '-',
      item_delivery_period: deliveryTime || '-',
      item_list_name: collection?.title || template || '-',
      item_list_id: collection?.id || collection?.handle || '-',
      index,
      quantity,
      price: price / 100,
    };
  } catch (error) {
    if (typeof window.cnvs.ReportError !== 'function') {
      window.cnvs.ReportError(
        'There was an error while formatting tracking items.',
        error,
      );
    }
    return {};
  }
}
